export function formatDateYYYYMMDD(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseDateYYYYMMDD(value: string): Date | null {
  const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

export function extractIsoDateYYYYMMDD(iso: string): string | null {
  const m = /^([0-9]{4}-[0-9]{2}-[0-9]{2})T/.exec(iso);
  return m ? m[1] : null;
}

export function extractIsoOffset(iso: string): string | null {
  // Examples: 2026-01-05T17:00:00+03:00, 2026-01-05T14:00:00Z
  const m = /(Z|[+-][0-9]{2}:[0-9]{2})$/.exec(iso);
  return m ? m[1] : null;
}

export function formatIsoDateYYYYMMDD(iso: string): string {
  return extractIsoDateYYYYMMDD(iso) ?? formatDateYYYYMMDD(new Date(iso));
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function toIsoWithOffset(date: Date): string {
  const year = date.getFullYear();
  const month = pad2(date.getMonth() + 1);
  const day = pad2(date.getDate());
  const hours = pad2(date.getHours());
  const minutes = pad2(date.getMinutes());
  const seconds = pad2(date.getSeconds());

  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const offH = pad2(Math.floor(abs / 60));
  const offM = pad2(abs % 60);

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offH}:${offM}`;
}

export function toIsoUtc(date: Date): string {
  // Keep a stable format without milliseconds to match backend examples.
  const iso = date.toISOString();
  // 2026-01-05T14:00:00.000Z -> 2026-01-05T14:00:00Z
  const m = /^([0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2})\.[0-9]{3}Z$/.exec(iso);
  return m ? `${m[1]}Z` : iso;
}

export function toIsoWithFixedOffset(date: Date, offset: string): string {
  const year = date.getFullYear();
  const month = pad2(date.getMonth() + 1);
  const day = pad2(date.getDate());
  const hours = pad2(date.getHours());
  const minutes = pad2(date.getMinutes());
  const seconds = pad2(date.getSeconds());

  const normalized = offset === 'Z' ? 'Z' : offset;
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${normalized}`;
}

export function combineLocalDateAndTimeToIso(date: Date, timeHHMM: string): string | null {
  const m = /^([0-9]{2}):([0-9]{2})$/.exec(timeHHMM);
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  const d = new Date(date);
  d.setHours(hours, minutes, 0, 0);
  return toIsoWithOffset(d);
}

export function combineDateAndTimeToUtcIso(date: Date, timeHHMM: string): string | null {
  const m = /^([0-9]{2}):([0-9]{2})$/.exec(timeHHMM);
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  // Interpret selected date/time as a UTC timestamp for the same calendar day.
  // Using Date.UTC avoids day-shifts when the device is not in UTC.
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return toIsoUtc(new Date(Date.UTC(year, month, day, hours, minutes, 0, 0)));
}

export function combineDateAndTimeToIso(date: Date, timeHHMM: string, offset?: string): string | null {
  const m = /^([0-9]{2}):([0-9]{2})$/.exec(timeHHMM);
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  const d = new Date(date);
  d.setHours(hours, minutes, 0, 0);

  if (offset) {
    const normalized = offset === 'Z' || /^[+-][0-9]{2}:[0-9]{2}$/.test(offset) ? offset : null;
    if (normalized) return toIsoWithFixedOffset(d, normalized);
  }

  return toIsoWithOffset(d);
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function formatDateHuman(date: Date): string {
  return date.toLocaleDateString('ru-RU', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  });
}
